import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Permissions from '../../data/permissions';
import NotFoundPage from '../pages/notFoundPage';

// These Fallback components are just simple wrappers. Their only job is to be identifiable.
const PermissionFallback = ({ children }) => <>{children}</>;
const GateFallback = ({ children }) => <>{children}</>;
const Fallback = ({ children }) => <>{children}</>;

/**
 * A component that conditionally renders its children based on permissions.
 * It renders children only if the user has the required permission.
 * Otherwise, it renders null or a fallback component.
 */
function Protected(props) {
    const { isAllowed, gate, showNotFound, children } = props;
    let mainContent = [];
    let permissionFallbackContent = null;
    let gateFallbackContent = null;
    let fallbackContent = null;
  
    Children.forEach(children, (child) => {
        if (!child) return;

        // We check the component's type to see if it's our special Fallback components.
        if (child.type === PermissionFallback) {
            permissionFallbackContent = child;
        } else if (child.type === GateFallback) {
            gateFallbackContent = child;
        } else if (child.type === Fallback) {
            fallbackContent = child;
        } else {
            mainContent.push(child);
        }
    });

    if (!isAllowed) {
        if (permissionFallbackContent) return permissionFallbackContent;
        if (fallbackContent) return fallbackContent;
        return showNotFound ? <NotFoundPage /> : null;
    }
    
    if ('gate' in props && !gate) {
        if (gateFallbackContent) return gateFallbackContent;
        if (fallbackContent) return fallbackContent;
        return showNotFound ? <NotFoundPage /> : null;
    }

    return <>{mainContent}</>;
};

Protected.propTypes = {
    isAllowed: PropTypes.bool,
    gate: PropTypes.bool,
    showNotFound: PropTypes.bool,
    children: PropTypes.node,
    // The following props are read indirectly by mapStateToProps (not consumed directly here):
    // resource: PropTypes.string,
    // resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // action: PropTypes.string,
    // context: PropTypes.object
};

Protected.defaultProps = {
    isAllowed: false,
    showNotFound: false,
    children: null,
};

Protected.PermissionFallback = PermissionFallback;
Protected.GateFallback = GateFallback;
Protected.Fallback = Fallback;

const mapStateToProps = (state, ownProps) => {

    let {resource, resourceId, action, context} = ownProps;
    
    if(resourceId) {
        if(!context) context = {};
        context = {...context, resourceId};
    }

    return {
        isAllowed: Permissions.selectors.can(state, resource, action, context)
    };
};

export { Protected as UnconnectedProtected }; // For testing purposes
export default connect(mapStateToProps)(Protected);